// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { Category } from "@insite/client-framework/Services/CategoryService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCategoriesDataView } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import loadCategories from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategories";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import SectionWrapper, { SectionWrapperStyles } from "@pacific/App/Components/Common/SectionWrapper";
import XngCarousel, { SwiperStyle } from "@pacific/App/Components/XngCarousel/XngCarousel";
import { xIsAuthenticated } from "@pacific/App/Store/Selectors/GeneralSelectors";
import { getCustomTypeSession } from "@pacific/App/Store/Selectors/SessionSelectors";
import LazyImage, { LazyImageProps } from "@pacific/MobiusOverrides/LazyImage/LazyImage";
import { color, ellipsis, getSystemColor, layoutContainer } from "@pacific/XDL/Utilities";
import { xScreen } from "@pacific/XDL/Utilities";
import { getDataLayer } from "@pacific/XDL/Utilities/dataLayer";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    title = "title",
    selectedCategoryIds = "selectedCategoryIds",
    subtitle = "subtitle",
    subtitleUrl = "subtitleUrl",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.selectedCategoryIds]: string[];
        [fields.subtitle]: string;
        [fields.subtitleUrl]: LinkFieldValue;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    categoriesDataView: getCategoriesDataView(state),
    isAuthenticated: xIsAuthenticated(state),
    session: getCustomTypeSession(state),
    currentBillTo: getCurrentBillToState(state).value,
});

const mapDispatchToProps = {
    loadCategories,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface FeaturedCategoriesStyles {
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    sectionWrapperStyles?: SectionWrapperStyles;
    categoryWrapper?: InjectableCss;
    link?: LinkPresentationProps;
    image?: LazyImageProps;
    imageWrapper?: InjectableCss;
    swiperStyles?: SwiperStyle;
}

export const featuredCategoriesStyles: FeaturedCategoriesStyles = {
    centeringWrapper: {
        css: css`
            height: 215px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    categoryWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
        `,
    },
    imageWrapper: {
        css: css`
            width: 198px;
            height: 198px;
            border-top: 4px solid ${color("primary")};
            background-color: ${color("common.white")};
            ${xScreen("lt", "md")} {
                width: 166px;
                height: 166px;
            }
        `,
    },
    image: {
        height: "198px",
        transformations: {
            width: 198,
        },
        css: css`
            object-fit: cover;
            width: 100%;
        `,
    } as any,
    link: {
        color: "text.main",
        typographyProps: {
            variant: "h5",
            size: 18,
            css: css`
                margin-bottom: 0;
                margin-top: 16px;
                ${ellipsis(2)};
            `,
        },
    },
    sectionWrapperStyles: {
        title: {
            wrapper: {
                css: css`
                    margin-bottom: 45px;
                `,
            },
        },
        wrapper: {
            css: css`
                ${layoutContainer};
            `,
        },
    },
    swiperStyles: {
        container: {
            css: css`
                box-shadow: -90px 12px ${color("common.ui14")}, 90px 12px ${color("common.ui14")},
                    -300px 12px ${color("common.ui14")}, 300px 12px ${color("common.ui14")};
                background-color: ${color("common.ui14")};
                border-top: 12px solid ${color("common.background")};

                .swiper-container {
                    transform: translateY(-20px);
                }
            `,
        },
    },
};

const styles = featuredCategoriesStyles;

const FeaturedCategoriesWidget: React.FC<Props> = ({
    fields: { selectedCategoryIds, title, subtitle, subtitleUrl },
    loadCategories,
    categoriesDataView,
    currentBillTo,
    isAuthenticated,
    session,
}) => {
    const [selectedCategories, setSelectedCategories] = React.useState<Category[]>([]);

    React.useEffect(() => {
        if (!categoriesDataView.isLoading && !categoriesDataView.value) {
            loadCategories();
        }
    }, []);

    React.useEffect(() => {
        const tempCategories = categoriesDataView.value?.filter(
            category => selectedCategoryIds.indexOf(category.id) > -1,
        );
        if (tempCategories?.length) {
            setSelectedCategories(tempCategories);
        }
    }, [selectedCategoryIds, categoriesDataView]);

    if (!selectedCategoryIds || selectedCategoryIds.length === 0) {
        return null;
    }

    if (categoriesDataView.isLoading || !categoriesDataView.value) {
        return (
            <StyledWrapper {...styles.centeringWrapper} data-test-selector="shopByCategory_Loading">
                <LoadingSpinner {...styles.spinner} />
            </StyledWrapper>
        );
    }

    const _GaFeatureCategories = (text: string, destination: string, elementId: string) => {
        const dataLayer = getDataLayer();
        if (!dataLayer) {
            return;
        }
        const googleAnalyticsData = {
            event: "featured_categories",
            click_text: text,
            destination,
            elementId,
            authentication_state: isAuthenticated ? "Authenticated" : "Guest",
            affiliation: currentBillTo?.companyName || "",
            sales_rep: (session as any).salesperson?.name || "",
        };
        dataLayer.push(googleAnalyticsData);
    };

    return (
        <SectionWrapper
            title={translate(title)}
            extendedStyles={styles.sectionWrapperStyles}
            linkText={subtitle}
            link={subtitleUrl}
            onLinkClick={() => {
                _GaFeatureCategories(subtitle, "/Categories", "fc_view_all");
            }}
        >
            <XngCarousel
                navigationOutSide={true}
                slidesPerView={1}
                spaceBetween={35}
                extendedStyles={styles.swiperStyles}
                navigation={true}
                breakpoints={{
                    480: {
                        slidesPerView: 2,
                        spaceBetween: 24,
                    },
                    1024: {
                        slidesPerView: 4,
                        spaceBetween: 14,
                    },
                    1200: {
                        slidesPerView: 5,
                        spaceBetween: 35,
                    },
                }}
            >
                {selectedCategories?.map(category => (
                    <StyledWrapper key={category.id} {...styles.categoryWrapper}>
                        <Clickable href={category.path} {...styles.imageWrapper} aria-label="category image clickable">
                            <LazyImage
                                src={category.smallImagePath}
                                altText={category.shortDescription}
                                onClick={() => {
                                    _GaFeatureCategories(category.shortDescription, category.path, "fc_image");
                                }}
                                {...styles.image}
                            />
                        </Clickable>
                        <Link
                            href={category.path}
                            {...styles.link}
                            area-label={category.shortDescription}
                            onClick={() => {
                                _GaFeatureCategories(category.shortDescription, category.path, "fc_text");
                            }}
                        >
                            {category.shortDescription}
                        </Link>
                    </StyledWrapper>
                ))}
            </XngCarousel>
        </SectionWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(FeaturedCategoriesWidget),
    definition: {
        group: "Xngage Custom" as any,
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "Featured Categories",
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.selectedCategoryIds,
                editorTemplate: "CategoriesField",
                defaultValue: [],
                fieldType: "General",
                isRequired: true,
                validate: value => (value.length > 10 ? translate("You Can Add 10 Categories Only") : null),
            },
            {
                name: fields.subtitle,
                displayName: "Link Text",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.subtitleUrl,
                displayName: "Link Url",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                fieldType: "General",
                isRequired: true,
                isVisible: item => item.fields.subtitle,
            },
        ],
    },
};

export default widgetModule;
